import React, { memo, useEffect, useRef, useState } from "react";

import { PieChart, Pie, Cell, ResponsiveContainer, Label } from "recharts";

import Card from "../../../components/Card";
import LoadingElevaty from "../../../components/LoadingElevaty";
import { useClient } from "../../../hooks/ClientProvider";
import Api from "../../../services/api";
import designSystemColors from "../../../styles";
import * as S from "../style";
import LimitInformationContainer from "./LimitInformationContainer";
import { Tabs } from "../../../components-v2/Tabs";

function CustomLabel({ viewBox, value }) {
  const { cx, cy } = viewBox;
  const formattedValue = value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (
    <g>
      <text
        x={cx}
        y={cy - 25}
        className="total"
        textAnchor="middle"
        dominantBaseline="central"
      >
        TOTAL R$
      </text>
      <text
        x={cx}
        y={cy + 5}
        className="amount"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {formattedValue}
      </text>
    </g>
  );
}

function CustomLegend({ payload }) {
  return (
    <ul className="CustomLegendLimits">
      {payload.map((entry) => (
        <li
          key={entry.name}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginBottom: "25px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg height="20" width="20" style={{ marginRight: "10px" }}>
              <circle cx="10" cy="10" r="10" fill={entry.color} />
            </svg>
            <span style={{ fontWeight: "bold" }}>{entry.value}</span>
          </div>
          <p style={{ color: entry.color }}>
            {`R$ ${entry.payload.value.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </p>
        </li>
      ))}
    </ul>
  );
}

function CardsLimitChart() {
  const ref = useRef();
  const [loading, setLoading] = useState(false);
  const { client, isLoadingClient } = useClient();

  const [informationsClient, setInformationsClient] = useState({});

  const limitePrincipal = informationsClient?.limitePrincipal ?? 0;
  const limiteExtra = client?.client?.limiteExtra ?? 0;
  const saldoDisponivelExtra = client?.client?.saldoDisponivelExtra ?? 0;
  const utilizaLimiteExtra = client?.client?.utilizaLimiteExtra;
  const total = utilizaLimiteExtra
    ? limitePrincipal + limiteExtra
    : limitePrincipal;
  const saldoDisponivel = client?.client?.saldoDisponivel;
  const limiteExtraTotal = client?.client?.limiteExtraTotal;
  const devedorTotal = client?.client?.devedorTotal;
  const [refreshTrigger, setRefreshTrigger] = useState(
    sessionStorage.getItem("@SAC_force_update") || "0"
  );

  useEffect(() => {
    const handleStorageChange = (e) => {
      setRefreshTrigger(
        e.detail || sessionStorage.getItem("@SAC_force_update")
      );
    };

    window.addEventListener("storage_updated", handleStorageChange);
    return () =>
      window.removeEventListener("storage_updated", handleStorageChange);
  }, []);

  useEffect(() => {
    if (client) {
      setLoading(true);
      Api.get("client_information", { client_id: client.client.id })
        .then((response) => {
          setInformationsClient({
            limitePrincipal: response.data.limitePrincipal,
            saldoDisponivel: response.data.saldoDisponivel,
            limiteParcelado: response.data.limiteParcelado,
            saldoDisponivelParcelado: response.data.saldoDisponivelParcelado,
            limiteSaque: response.data.limiteSaque,
            saldoDisponivelSaque: response.data.saldoDisponivelSaque,
            limiteExtra: response.data.limiteExtra,
            utilizaLimiteExtra: response.data.utilizaLimiteExtra,
          });
        })
        .catch((error) => {
          console.error(
            "Erro ao obter informações do limite do cliente",
            error
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [client, refreshTrigger]);

  const limiteUtilizadoRotativo = limitePrincipal - saldoDisponivel;
  const limiteUtilizadoRotativoComExtra =
    limiteExtraTotal - saldoDisponivelExtra;

  const chartDataRotativo = [
    {
      name: "DISPONÍVEL",
      value: utilizaLimiteExtra ? saldoDisponivelExtra : saldoDisponivel || 0,
    },
    {
      name: "UTILIZADO",
      value: utilizaLimiteExtra
        ? limiteUtilizadoRotativoComExtra
        : limiteUtilizadoRotativo,
    },
  ];

  const limiteUtilizadoParcelado =
    informationsClient.limiteParcelado -
    informationsClient.saldoDisponivelParcelado;

  const chartDataParcelado = [
    {
      name: "DISPONÍVEL",
      value: parseFloat(informationsClient.saldoDisponivelParcelado) || 0,
    },
    {
      name: "UTILIZADO",
      value: limiteUtilizadoParcelado > 0 ? limiteUtilizadoParcelado : 0,
    },
  ];

  const limiteUtilizadoSaque =
    informationsClient.limiteSaque - informationsClient.saldoDisponivelSaque;

  const chartDataSaque = [
    {
      name: "DISPONÍVEL",
      value: parseFloat(informationsClient.saldoDisponivelSaque) || 0,
    },
    {
      name: "UTILIZADO",
      value: limiteUtilizadoSaque > 0 ? limiteUtilizadoSaque : 0,
    },
  ];

  const isSaldoNegativo =
    (limiteExtra < 0 && saldoDisponivel < 0) ||
    (limiteExtra >= 0 && saldoDisponivelExtra < 0);

  const rotativeColors = [
    isSaldoNegativo
      ? designSystemColors.errorColor
      : designSystemColors.activateGreen,
    designSystemColors.cancelButtons,
  ];

  const COLORS = [
    designSystemColors.cancelButtons,
    designSystemColors.lightOrange,
  ];

  const outerRadii = {
    DISPONÍVEL: 80,
    UTILIZADO: 90,
  };

  return (
    <>
      <S.WrapperCardLimit ref={ref}>
        {loading || isLoadingClient ? (
          <S.WrapperNewLoading>
            <LoadingElevaty />
          </S.WrapperNewLoading>
        ) : (
          <Card title="Limites do cliente" modalTitle="Limites do cliente">
            <Tabs
              tabs={[
                {
                  id: "tab-limite-rotativo",
                  title: "Rotativo",
                  content: (
                    <ResponsiveContainer
                      width="100%"
                      height={utilizaLimiteExtra ? 400 : 300}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PieChart width={218} height={218}>
                          <Pie
                            data={chartDataRotativo}
                            cx={90}
                            cy={90}
                            innerRadius={62}
                            startAngle={90}
                            endAngle={450}
                            paddingAngle={1}
                            dataKey="value"
                            labelLine={false}
                          >
                            {chartDataRotativo.map((entry, index) => (
                              <Cell
                                key={`cell-${entry.name}`}
                                fill={
                                  rotativeColors[index % rotativeColors.length]
                                }
                                stroke={
                                  rotativeColors[index % rotativeColors.length]
                                }
                                strokeWidth={entry.name === "UTILIZADO" ? 6 : 1}
                                outerRadius={outerRadii[entry.name]}
                              />
                            ))}
                            <Label
                              content={
                                <CustomLabel
                                  value={
                                    limiteExtra > 0
                                      ? limitePrincipal + limiteExtra
                                      : limitePrincipal
                                  }
                                />
                              }
                              position="center"
                            />
                          </Pie>
                        </PieChart>
                        <CustomLegend
                          payload={chartDataRotativo.map((entry, index) => ({
                            value: entry.name,
                            color:
                              rotativeColors[index % rotativeColors.length],
                            payload: entry,
                          }))}
                        />
                      </div>
                      <LimitInformationContainer
                        utilizaLimiteExtra={utilizaLimiteExtra}
                        devedorTotal={devedorTotal}
                        limiteExtra={limiteExtra}
                        limitePrincipal={limitePrincipal}
                        saldoDisponivel={saldoDisponivel}
                        saldoDisponivelExtra={saldoDisponivelExtra}
                        total={total}
                      />
                    </ResponsiveContainer>
                  ),
                },
                {
                  id: "tab-limite-parcelado",
                  title: "Parcelado",
                  content: (
                    <ResponsiveContainer width="100%" height={216}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PieChart width={218} height={218}>
                          <Pie
                            data={chartDataParcelado}
                            cx={90}
                            cy={90}
                            innerRadius={62}
                            startAngle={90}
                            endAngle={450}
                            paddingAngle={1}
                            dataKey="value"
                            labelLine={false}
                          >
                            {chartDataParcelado.map((entry, index) => (
                              <Cell
                                key={`cell-${entry.name}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={entry.name === "UTILIZADO" ? 6 : 1}
                                outerRadius={outerRadii[entry.name]}
                              />
                            ))}
                            <Label
                              content={
                                <CustomLabel
                                  value={informationsClient.limiteParcelado}
                                />
                              }
                              position="center"
                            />
                          </Pie>
                        </PieChart>
                        <CustomLegend
                          payload={chartDataParcelado.map((entry, index) => ({
                            value: entry.name,
                            color: COLORS[index % COLORS.length],
                            payload: entry,
                          }))}
                        />
                      </div>
                    </ResponsiveContainer>
                  ),
                },
                {
                  id: "tab-limite-saque",
                  title: "Saque",
                  content: (
                    <ResponsiveContainer width="100%" height={216}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <PieChart width={218} height={218}>
                          <Pie
                            data={chartDataSaque}
                            cx={90}
                            cy={90}
                            innerRadius={62}
                            startAngle={90}
                            endAngle={450}
                            paddingAngle={1}
                            dataKey="value"
                            labelLine={false}
                          >
                            {chartDataSaque.map((entry, index) => (
                              <Cell
                                key={`cell-${entry.name}`}
                                fill={COLORS[index % COLORS.length]}
                                stroke={COLORS[index % COLORS.length]}
                                strokeWidth={entry.name === "UTILIZADO" ? 6 : 1}
                                outerRadius={outerRadii[entry.name]}
                              />
                            ))}
                            <Label
                              content={
                                <CustomLabel
                                  value={informationsClient.limiteSaque}
                                />
                              }
                              position="center"
                            />
                          </Pie>
                        </PieChart>
                        <CustomLegend
                          payload={chartDataSaque.map((entry, index) => ({
                            value: entry.name,
                            color: COLORS[index % COLORS.length],
                            payload: entry,
                          }))}
                        />
                      </div>
                    </ResponsiveContainer>
                  ),
                },
              ].filter(Boolean)}
            />
          </Card>
        )}
      </S.WrapperCardLimit>
    </>
  );
}

export default memo(CardsLimitChart);
